import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
  ReferenceLine,
} from "recharts";
import {
  ValueType,
  NameType,
} from "recharts/types/component/DefaultTooltipContent";
import { FaRegFilePdf } from "react-icons/fa6";
import { TbFileExcel } from "react-icons/tb";
import { FaRegCopy } from "react-icons/fa";
import { BsFiletypeCsv } from "react-icons/bs";
import { exportChartTableToExcel } from "./ExcelCurved";
import { exportTableToExcel } from "./ExcelCurved";
import { exportChartToPDF, useExportLogo } from "./ExcelCurved";
import { copyChartToClipboard, exportTableToCSV } from "./ExcelCurved";
import { useAssetContext } from "../../DataContext/AssetContext";
import { FaSearchMinus } from "react-icons/fa";
import { useDevice } from "./Device-Responsive";
import { MetricGroup } from "../CardAssets/MetricConfig_type";
import { DateTime } from "luxon";
import { buildMetricUnitMap } from "./metricUtils";
import { useMetricConfig } from "./useMetricConfig";


interface ChartProps {
  chartData: Array<any>;
  metrics: Array<string>;
  xAxis: string;
  legend: Array<string>;
  xAxisLabel: string;
  yAxisLabel: string;
  selectedFrequency: string;
  selectedDateRange: string;
  onVisibilityChange: (visibility: Record<string, boolean>) => void;
  defaultVisibility: Record<string, boolean>;
  useIntervalAggregation: boolean;
}

const predefinedColors = [
  "#F6866A",
  "#59E6F6",
  "#8A6CE3",
  "#FABE7A",
  "#2C3192",
  "#7661E2",
];

const generateUniqueColors = (count: number) => {
  return Array.from(
    { length: count },
    (_, i) => `hsl(${(i * 137.5) % 360}, 70%, 50%)`
  );
};

interface CustomTooltipProps extends TooltipProps<ValueType, NameType> {
  selectedFrequency: string;
  useIntervalAggregation: boolean;
    metricUnitMap: Record<string, string>;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  selectedFrequency,
  useIntervalAggregation,
  metricUnitMap,
}) => {
  if (active && payload && payload.length) {
    const filteredPayload = payload.filter(
      (entry) => entry.value !== null
    );

    if (filteredPayload.length === 0) return null;

    const dt = DateTime.fromISO(label, { setZone: true });
    let formattedLabel = "";

    if (useIntervalAggregation) {
      if (["hour", "5minute", "15minute"].includes(selectedFrequency)) {
        formattedLabel = dt.toFormat("hh:mm a");
      } else {
        formattedLabel = dt.toFormat("MM/dd/yyyy");
      }
    } else {
      if (["day", "week", "month"].includes(selectedFrequency)) {
        formattedLabel = dt.toFormat("MM/dd/yyyy");
      } else {
        formattedLabel = dt.toFormat("MM/dd/yyyy hh:mm a");
      }
    }
const formatNumber = (
  value: any
) =>
  Number(value).toLocaleString(
    undefined,
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );
    return (
      <div className="bg-white shadow-lg rounded-md p-3 border border-gray-300">
        <p className="text-gray-900 font-semibold text-sm mb-1">
          {formattedLabel}
        </p>
        <div className="space-y-1">
          {filteredPayload.map((entry, index) => (
            <div key={`item-${index}`} className="flex items-center">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full mr-2"
                style={{ backgroundColor: entry.color }}
              ></span>
              <span className="text-xs text-gray-700">
                {/* {entry.name}:{" "} */}
                {entry.name}
                {entry.name && metricUnitMap?.[entry.name]
                  ? ` (${metricUnitMap[entry.name]})`
                  : ""}:
                <span className="font-semibold text-gray-900">
                  {/* {entry.value?.toLocaleString()} */}
                  {formatNumber(entry.value)}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

const StepLineChart: React.FC<ChartProps> = ({
  chartData,
  metrics,
  xAxis,
  legend,
  xAxisLabel,
  yAxisLabel,
  selectedFrequency,
  onVisibilityChange,
  defaultVisibility,
  useIntervalAggregation,
  selectedDateRange,
}) => {
  const extraColors = generateUniqueColors(
    Math.max(0, metrics.length - predefinedColors.length)
  );
  const [fullData, setFullData] = useState(chartData);
  const panAmount = 1;
  const chartRef = useRef<HTMLDivElement>(null);
  const [displayedData, setDisplayedData] = useState(chartData);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [dragStartX, setDragStartX] = useState<number | null>(null);
  const [zoomStack, setZoomStack] = useState<{ start: number; end: number }[]>(
    []
  );
  const [isScrollEnabled, setIsScrollEnabled] = useState(false);
  const handleEnableScroll = () => {
    setIsScrollEnabled(true);
  };
  const { selectedAssetId } = useAssetContext();
  // const prevAssetRef = useRef<string | number | null>(null);
  const { isSmallDevice } = useDevice();
  useEffect(() => {
    setFullData(chartData);
    setDisplayedData(chartData);
    setZoomLevel(1);
  }, [chartData, selectedFrequency, selectedDateRange]);

  const zoomIn = (startIndex: number, endIndex: number) => {
    const MIN_POINTS = 10;
    const ZOOM_FACTOR = 0.5;
    const fullStartIndex = fullData.indexOf(displayedData[0]);
    const fullEndIndex = fullData.indexOf(
      displayedData[displayedData.length - 1]
    );

    startIndex = fullStartIndex + startIndex;
    endIndex = fullStartIndex + endIndex;
    const range = endIndex - startIndex;
    if (range < MIN_POINTS) {
      startIndex = Math.max(0, startIndex - Math.floor(MIN_POINTS / 2));
      endIndex = Math.min(
        fullData.length,
        endIndex + Math.floor(MIN_POINTS / 2)
      );
    }

    const step = Math.max(
      Math.floor((endIndex - startIndex) * ZOOM_FACTOR),
      MIN_POINTS
    );
    const newStart =
      startIndex + Math.floor((endIndex - startIndex - step) / 2);
    const newEnd = newStart + step;
    setZoomStack((prevStack) => [
      ...prevStack,
      { start: fullStartIndex, end: fullEndIndex },
    ]);

    setDisplayedData(fullData.slice(newStart, newEnd));
    setZoomLevel((prevZoom) => prevZoom + 1);
  };

  const zoomOut = () => {
    if (zoomStack.length === 0) {
      setDisplayedData(fullData);
      setIsScrollEnabled(false);
      setZoomLevel(1);
      return;
    }

    const lastZoom = zoomStack[zoomStack.length - 1];
    setDisplayedData(fullData.slice(lastZoom.start, lastZoom.end));

    setZoomStack((prevStack) => prevStack.slice(0, -1));
    setZoomLevel((prevZoom) => prevZoom - 1);
  };
  const panLeft = () => {
    const fullStartIndex = fullData.indexOf(displayedData[0]);
    const newStart = Math.max(0, fullStartIndex - panAmount);
    const newEnd = newStart + displayedData.length;
    setDisplayedData(fullData.slice(newStart, newEnd));
  };
  const panRight = () => {
    const fullStartIndex = fullData.indexOf(displayedData[0]);
    const newEnd = Math.min(
      fullData.length,
      fullStartIndex + displayedData.length + panAmount
    );
    const newStart = newEnd - displayedData.length;
    setDisplayedData(fullData.slice(newStart, newEnd));
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    setDragStartX(event.clientX);
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (dragStartX !== null) {
      const diff = event.clientX - dragStartX;
      if (diff > 20) {
        panLeft();
        setDragStartX(event.clientX);
      } else if (diff < -20) {
        panRight();
        setDragStartX(event.clientX);
      }
    }
  };

  const handleMouseUp = () => {
    setDragStartX(null);
  };
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") panLeft();
      if (event.key === "ArrowRight") panRight();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [displayedData, fullData]);

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      if (!isScrollEnabled) return; // Scroll only if enabled

      event.preventDefault();
      const isShiftPressed = event.shiftKey;
      const scrollDirection = event.deltaY;

      if (isShiftPressed) {
        if (scrollDirection > 0) {
          panRight();
        } else {
          panLeft();
        }
      } else {
        if (scrollDirection < 0) {
          zoomIn(0, displayedData.length / 2);
        } else {
          zoomOut();
        }
      }
    };
    const chartElement = chartRef.current;
    if (!chartElement) return;

    chartElement.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      chartElement.removeEventListener("wheel", handleWheel);
    };
  }, [isScrollEnabled, displayedData]);

  const uniqueColors = [...predefinedColors, ...extraColors];

  const [visibility, setVisibility] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!defaultVisibility || Object.keys(defaultVisibility).length === 0) return;

    setVisibility((prev) => {
      if (Object.keys(prev).length === 0) {
        return defaultVisibility;
      }

      const next = { ...prev };
      let changed = false;

      metrics.forEach((metric) => {
        if (!(metric in next)) {
          next[metric] = !!defaultVisibility[metric];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [defaultVisibility, metrics]);
  
  // useEffect(() => {
  //   if (Object.keys(visibility).length === 0) {
  //     const initialVisibility = metrics.reduce((acc, metric) => {
  //       acc[metric] =
  //         defaultVisibility?.[metric] !== undefined
  //           ? defaultVisibility[metric]
  //           : metric === "DA_AWD" || metric === "METERED_OUTPUT";
  //       return acc;
  //     }, {} as Record<string, boolean>);
  //     setVisibility(initialVisibility);
  //   }
  // }, [defaultVisibility, metrics]);
  // useEffect(() => {
  //   if (!metrics || metrics.length === 0) return;
  
  //   const initialVisibility: Record<string, boolean> = {};
  
  //   metrics.forEach(metric => {
  //     initialVisibility[metric] =
  //       defaultVisibility?.[metric] ??
  //       (metric === "DA_AWD" || metric === "METERED_OUTPUT");
  //   });
  
  //   setVisibility(initialVisibility);
  // }, [selectedAssetId]);  
  

  const toggleVisibility = (metric: string) => {
    let updatedVisibility: Record<string, boolean> | null = null;
    setVisibility((prev) => {
      updatedVisibility = { ...prev, [metric]: !prev[metric] };
      return updatedVisibility;
    });
    if (updatedVisibility) {
      onVisibilityChange(updatedVisibility);
    }
  };

  // const toggleAll = (selectAll: boolean, metricList: string[]) => {
  //   const updatedVisibility = { ...visibility };
  //   metricList.forEach((metric) => {
  //     updatedVisibility[metric] = selectAll;
  //   });
  //   setVisibility(updatedVisibility);
  //   onVisibilityChange(updatedVisibility);
  // };
  const toggleAll = (selectAll: boolean, metricList: string[]) => {
    let updatedVisibility: Record<string, boolean> | null = null;
    setVisibility(prev => {
      const updated = { ...prev };
  
      metricList.forEach(metric => {
        updated[metric] = selectAll;
      });
  
      updatedVisibility = updated;
      return updated;
    });
    if (updatedVisibility) {
      onVisibilityChange(updatedVisibility);
    }
  };
  
  // const toggleAll = (selectAll: boolean, metricList: string[]) => {
  //   setVisibility(prev => {
  //     const updatedVisibility = { ...prev };
  //     metricList.forEach((metric) => {
  //       updatedVisibility[metric] = selectAll;
  //     });
  //     onVisibilityChange(updatedVisibility);
  //     return updatedVisibility;
  //   });
  // };
  
  // useEffect(() => {
  //   const shouldReset =
  //     Object.keys(visibility).length === 0 ||
  //     !metrics.every((m) => m in visibility);
  //   if (
  //     shouldReset &&
  //     defaultVisibility &&
  //     Object.keys(defaultVisibility).length > 0
  //   ) {
  //     setVisibility(defaultVisibility);
  //   }
  // }, [defaultVisibility, metrics]);

  const { volumeData, metricConfigData } = useAssetContext();
  const { metadata } = useAssetContext();  
  const { data: metricConfig } = useMetricConfig();
  const metricUnitMap = buildMetricUnitMap(metricConfig);
  const { logo } = useExportLogo();
  const getCustomRangeDays = (rangeStr: string): number | null => {
    const match = rangeStr.match(
      /^(\d{4}-\d{2}-\d{2})\s*-\s*(\d{4}-\d{2}-\d{2})$/
    );
    if (!match) return null;

    const [_, startStr, endStr] = match;
    const start = new Date(startStr);
    const end = new Date(endStr);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const generateIntervalMapping = (
    selectedDateRange: string
  ): Record<string, number> => {
    const maxLabels = 31;

    const calcInterval = (totalUnits: number): number => {
      return totalUnits <= maxLabels ? 1 : Math.ceil(totalUnits / maxLabels);
    };

    const getDynamicDayCounts = (range: string): number => {
      const today = new Date();
      const startOfYear = new Date(today.getFullYear(), 0, 1);
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

      const msInDay = 1000 * 60 * 60 * 24;

      if (range === "ytd") {
        return (
          Math.floor((today.getTime() - startOfYear.getTime()) / msInDay) + 1
        );
      }

      if (range === "mtd") {
        return (
          Math.floor((today.getTime() - startOfMonth.getTime()) / msInDay) + 1
        );
      }

      return predefinedRanges[range] ?? getCustomRangeDays(range) ?? 0;
    };

    const predefinedRanges: Record<string, number> = {
      minus_7d: 7,
      minus_30d: 30,
      yesterday: 1,
      today: 1,
    };

    const days = getDynamicDayCounts(selectedDateRange);
    const totalDays = days;
    const totalWeeks = Math.floor(days / 7);
    // const totalMonths = Math.max(new Date().getMonth() + 1, 1); // Jan = 0

    const total15MinIntervals = totalDays * 96;
    const total5MinIntervals = totalDays * 288;
    const totalHourIntervals = totalDays * 24;
    const totalHours = totalDays * 24;
    const calcHourAlignedInterval = (pointsPerHour: number): number => {
      const hoursPerTick = Math.max(1, Math.ceil(totalHours / maxLabels));
      return hoursPerTick * pointsPerHour - 1;
    };

    const mapping: Record<string, number> = {
      day: calcInterval(totalDays),
      week: calcInterval(totalWeeks),
      // month: calcInterval(totalMonths),
      "15minute": calcHourAlignedInterval(4),
      "5minute": calcHourAlignedInterval(12),
      hour: calcHourAlignedInterval(1),
    };

    return mapping;
  };

  const intervalMapping = generateIntervalMapping(selectedDateRange);
  const interval = !useIntervalAggregation
    ? intervalMapping[selectedFrequency] ?? 0
    : 0;
  const cleanedChartData = useMemo(() => {
    const lastSeenValues: Record<string, number | null> = {};

    return chartData.map((dataPoint) => {
      const newPoint = { ...dataPoint };

      metrics.forEach((metric) => {
        const rawValue = newPoint[metric];

        if (rawValue === undefined || rawValue === null) {
          newPoint[metric] =
            lastSeenValues[metric] !== undefined ? lastSeenValues[metric] : null;
          return;
        }

        const numericValue =
          typeof rawValue === "number" ? rawValue : Number(rawValue);

        if (Number.isNaN(numericValue)) {
          newPoint[metric] =
            lastSeenValues[metric] !== undefined ? lastSeenValues[metric] : null;
          return;
        }

        newPoint[metric] = numericValue;
        lastSeenValues[metric] = numericValue;
      });

      return newPoint;
    });
  }, [chartData, metrics]);

  const [stableInterval, setStableInterval] = useState(interval);
  const prevIntervalRef = useRef(interval);

  useEffect(() => {
    if (displayedData.length === 0) return;

    const newInterval =
      zoomLevel === 1
        ? interval === 1
          ? 0
          : interval
        : zoomLevel === 2
        ? Math.ceil(displayedData.length / 30)
        : zoomLevel === 3
        ? Math.ceil(displayedData.length / 45)
        : zoomLevel === 4
        ? Math.ceil(displayedData.length / 60)
        : zoomLevel === 5
        ? Math.ceil(displayedData.length / 75)
        : prevIntervalRef.current;

    const isZoomingIn = newInterval > prevIntervalRef.current;
    prevIntervalRef.current = newInterval;

    if (isZoomingIn) {
      requestAnimationFrame(() => setStableInterval(newInterval));
    } else {
      React.startTransition(() => {
        setStableInterval(newInterval);
      });
    }
  }, [displayedData, zoomLevel]);

  const generateDynamicTicks = (
    min: number,
    max: number,
    steps: number = 4,
    existingTicks: number[] = []
  ) => {
    if (min === max) return [min];

    const range = max - min;
    const rawStep = range / steps;

    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const residual = rawStep / magnitude;
    let niceStep: number;
    if (residual >= 5) niceStep = 5 * magnitude;
    else if (residual >= 2) niceStep = 2 * magnitude;
    else niceStep = magnitude;

    const niceMin = Math.floor(min / niceStep) * niceStep;
    const niceMax = Math.ceil(max / niceStep) * niceStep;

    const customTicks: number[] = [];
    for (let t = niceMin; t <= niceMax; t += niceStep) {
      const decimals = Math.max(0, -Math.floor(Math.log10(niceStep)));
      const rounded = parseFloat(t.toFixed(decimals + 2));
      customTicks.push(rounded);
    }

    if (min < 0 && max > 0 && !customTicks.includes(0)) {
      customTicks.push(0);
    }

    const allTicks = [...customTicks, ...existingTicks];

    return [...new Set(allTicks)]
      .filter((t) => t >= min && t <= max)
      .sort((a, b) => a - b);
  };

  const dataSource = (displayedData && displayedData.length > 0)
    ? displayedData
    : cleanedChartData;

  const visibleValues: number[] = dataSource.flatMap((point) =>
    metrics.flatMap((metric) => {
      const val = point[metric];
      return visibility[metric] && typeof val === "number" ? [val] : [];
    })
  );

  let dataMin = 0;
  let dataMax = 0;
  if (visibleValues.length > 0) {
    dataMin = Math.min(...visibleValues);
    dataMax = Math.max(...visibleValues);
  } else {
    const fallbackValues = cleanedChartData.flatMap((d) =>
      metrics.map((m) => (typeof d[m] === "number" ? d[m] : NaN)).filter((v) => !isNaN(v))
    );
    if (fallbackValues.length > 0) {
      dataMin = Math.min(...fallbackValues);
      dataMax = Math.max(...fallbackValues);
    } else {
      dataMin = 0;
      dataMax = 1;
    }
  }

  let padding = (dataMax - dataMin) * 0.1;
  if (padding === 0) {
    padding = Math.abs(dataMax) * 0.1 || 1;
  }

  const yMin = dataMin - padding;
  const yMax = dataMax + padding;  

const visibleMetrics = metrics.filter((metric) => visibility[metric]);

const filteredExportData = displayedData.map((row) => {
  // const formattedDate = row[xAxis];
  const formatChartDate = (value: string) => {
    const dt = DateTime.fromISO(value, { setZone: true });
  
    if (["day", "week", "month"].includes(selectedFrequency)) {
      return dt.toFormat("MM/dd/yyyy");
    }
  
    return value.includes("T")
      ? dt.toFormat("MM/dd/yyyy hh:mm a")
      : dt.toFormat("MM/dd/yyyy");
  };
    // const formattedDate = row[xAxis];
    const formattedDate = formatChartDate(row[xAxis])

  const filteredRow: Record<string, any> = {
    [xAxisLabel]: formattedDate,
  };

  visibleMetrics.forEach((metric) => {
    filteredRow[metric] = row[metric];
  });

  return filteredRow;
});
const groupedHeaders = visibleMetrics.map(() => "Metrics"); 
const metricHeaders = visibleMetrics;
  return (
    <div className="bg-white p-2 lg:p-6 font-poppins relative">
      {/* </div>  <div ref={chartRef}> */}
      <div className="flex justify-end space-x-4 relative -top-16 ">
        <FaRegFilePdf
          className="text-gray-500 hover:text-red-500 text-xl transform hover:scale-110 transition cursor-pointer"
          title="Export as PDF"
          onClick={() =>
            exportChartToPDF(
              "chart-container",
              "VolumeTabReport",
              "VOLUME DATA",
              volumeData,
              logo
            )
          }
        />
        <BsFiletypeCsv
          className="text-gray-500 hover:text-red-500 text-xl transform hover:scale-110 transition"
          title="Export as CSV"
          // onClick={() => exportTableToCSV(chartData, "PriceReport")}
          
                    onClick={() => exportTableToCSV(filteredExportData, "exported_data")}

        />

        <TbFileExcel
          className="text-gray-500 hover:text-red-500 text-xl transform hover:scale-110 transition"
          title="Export as Excel"
          onClick={() =>
            exportTableToExcel(
              "VolumeTabReport",
              "VOLUME DATA",
              volumeData,
              logo,
              groupedHeaders,
              metricHeaders,
              filteredExportData
            )
          }
        />
        <FaRegCopy
          className="text-gray-500 hover:text-red-500 text-xl transform hover:scale-110 transition"
          title="Copy Data"
          onClick={() =>
            copyChartToClipboard("chart-container", chartData, volumeData)
          }
        />
      </div>
      <div id="chart-container">
        <div ref={chartRef}>
          <div
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onClick={handleEnableScroll}
          >
            <div className="flex justify-end space-x-4 ">
              {zoomLevel > 1 && (
                <button
                  onClick={zoomOut}
                  className="flex items-center px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-700"
                >
                  <FaSearchMinus className="mr-2" /> Zoom Out
                </button>
              )}
            </div>

            <ResponsiveContainer width="100%" height={400}>
              <LineChart
                data={zoomLevel > 1 ? displayedData : cleanedChartData}
                onClick={(event: any) => {
                  if (!event || !event.activeLabel) return;
                  const clickedIndex = displayedData.findIndex(
                    (d) => d[xAxis] === event.activeLabel
                  );

                  if (clickedIndex !== -1) {
                    // console.log("Clicked Index:", clickedIndex);
                    const rangeSize = Math.floor(displayedData.length / 2);
                    const start = Math.max(
                      0,
                      clickedIndex - Math.floor(rangeSize / 2)
                    );
                    const end = Math.min(
                      displayedData.length,
                      clickedIndex + Math.floor(rangeSize / 2)
                    );

                    zoomIn(start, end);
                  }
                }}
                margin={{
                  top: 0,
                  right: isSmallDevice ? 20 : 40,
                  bottom: isSmallDevice ? 80 : 90,
                  left: isSmallDevice ? 35 : 60,
                }}
              >
                <XAxis
                  dataKey="time"
                  tickFormatter={(value: string, index: number) => {
                    const dt = DateTime.fromISO(value, { setZone: true });
                
                    if (
                      selectedFrequency === "day" &&
                      selectedDateRange === "ytd" &&
                      useIntervalAggregation
                    ) {
                      if (displayedData.length > 90 && index % 7 !== 0) return "";
                      return dt.toFormat("MM/dd/yyyy");
                    }
                
                    if (["day", "week", "month"].includes(selectedFrequency)) {
                      return dt.toFormat("MM/dd/yyyy");
                    }
                
                    if (!useIntervalAggregation) {
                      return dt.toFormat("MM/dd/yyyy hh:mm a"); // AM/PM included
                    }
                
                    if (["15minute", "5minute", "hour"].includes(selectedFrequency)) {
                      return dt.minute === 0 ? dt.toFormat("hh:mm a") : "";
                    }
                
                    return dt.toFormat("MM/dd/yyyy");
                  }}
                  tick={{ fontSize: isSmallDevice ? 12 : 14 }}
                  angle={displayedData.length > 20 ? -30 : -30}
                  textAnchor={displayedData.length > 20 ? "end" : "middle"}
                  interval={isSmallDevice ? "preserveStartEnd" : stableInterval}
                  tickCount={
                    isSmallDevice
                      ? Math.min(displayedData.length, 8)
                      : Math.min(displayedData.length, 15)
                  }
                  dy={isSmallDevice ? 20 : displayedData.length > 20 ? 10 : 40}
                  label={{
                    value: xAxisLabel,
                    position: "bottom",
                    offset: isSmallDevice ? 70 : 70,
                    fontSize: isSmallDevice ? 12 : 16,
                    fontWeight: "bold",
                  }}
                />
                <YAxis
                  domain={[yMin, yMax]}
                  tickFormatter={(value) => {
                    if (Math.abs(value) >= 1_000_000) {
                      return (value / 1_000_000).toFixed(0) + "M";
                    } else if (Math.abs(value) >= 1_000) {
                      return (value / 1_000).toFixed(0) + "k";
                    }
                    return value.toString();
                  }}
                  tick={{ fontSize: isSmallDevice ? 12 : 14 }}
                  ticks={generateDynamicTicks(yMin, yMax, 6)}
                  label={{
                    value: yAxisLabel,
                    angle: -90,
                    position: "insideLeft",
                    dy: 40,
                    dx: -20,
                    style: {
                      textAnchor: "middle",
                      fontSize: isSmallDevice ? 12 : 14,
                      fontWeight: "bold",
                    },
                  }}
                />

                {Math.min(
                  ...cleanedChartData.flatMap((d) =>
                    metrics.map((m) => d[m] || 0)
                  )
                ) < 0 && (
                  <ReferenceLine
                    y={0}
                    stroke="lightgrey"
                    strokeDasharray="3 3"
                  />
                )}

                <Tooltip
                  content={
                    <CustomTooltip
                      selectedFrequency={selectedFrequency}
                      useIntervalAggregation={useIntervalAggregation}
                      metricUnitMap={metricUnitMap}
                    />
                  }
                />

                {metrics.map((metric, index) => {
                  const hasValidData = chartData.some(
                    (dataPoint) =>
                      dataPoint[metric] !== undefined &&
                      dataPoint[metric] !== null
                  );

                  return visibility[metric] && hasValidData ? (
                    <Line
                      key={metric}
                      type="monotone"
                      dataKey={metric}
                      stroke={uniqueColors[index]}
                      strokeWidth={2.5}
                      dot={false}
                      activeDot={false}
                      connectNulls={true}
                    />
                  ) : null;
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Custom Legend */}
        <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-8 mt-8 mb-8">
          {metricConfigData
            .filter((group: MetricGroup) => group.metric_list.length > 0)
            .map((group: MetricGroup, idx: number) => {
              const title = group.group_key || "Others";
              const list = group.metric_list;

              return (
                <div
                  key={idx}
                  className="flex flex-col border rounded-lg p-4 bg-gray-100 relative h-full"
                >
                  {/* Group title */}
                  <h4 className="text-lg text-gray-600 font-semibold mb-4 mt-2">
                    {title}
                  </h4>

                  {/* Metric checkboxes */}
                  <div className="flex flex-wrap gap-4">
                    {list.map((metric) => {
                      const metricIndex = metrics.indexOf(metric);
                      const color =
                        metricIndex >= 0 ? uniqueColors[metricIndex] : "#999";
                      const label =
                        metricIndex >= 0 ? legend?.[metricIndex] : metric;

                      return (
                        <label
                          key={metric}
                          className="flex items-center gap-2 min-w-[160px]"
                        >
                          <input
                            type="checkbox"
                            checked={!!visibility[metric]}
                            onChange={() => toggleVisibility(metric)}
                            className="peer w-4 h-4 accent-violet-500"
                          />
                          <span className="flex items-center text-xs lg:text-sm text-gray-600">
                            <span
                              className="inline-block w-4 h-[2px] mr-2"
                              style={{ backgroundColor: color }}
                            />
                            {/* {label} */}
                             {label}
                      {metricUnitMap[metric] ? ` (${metricUnitMap[metric]})` : ""}
                          </span>
                        </label>
                      );
                    })}
                  </div>

                  {/* Group-level Select All */}
                  <button
                    onClick={() => {
                      const allSelected = list.every((m) => visibility[m]);
                      toggleAll(!allSelected, list);
                    }}
                    className="absolute top-4 right-4 flex items-center gap-1 px-2 py-1 bg-gray-500 text-white text-xs font-medium rounded-md hover:bg-gray-700 transition duration-300"
                  >
                    {list.every((m) => visibility[m])
                      ? "Select None"
                      : "Select All"}
                  </button>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default StepLineChart;
